import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { Plus, Trash2, Save, Loader2, Info } from 'lucide-react';

/**
 * Interactive genogram for Week 6 + Week 7 Self-Work.
 *
 * Architecture
 *   - canvas-free, pure React + SVG so it renders cleanly on iOS Safari.
 *   - nodes = people  ({ id, label, role, gender, addiction, deceased, x, y })
 *   - edges = relationships  ({ from, to, kind })  where kind is one of
 *       parent  spouse  sibling  child  estranged  conflict  cutoff  close
 *   - autosaves to /api/self-work/genogram/{week} every 3 seconds when dirty.
 *
 * The legend below maps the visual symbols used.
 */

const ROLES = ['self', 'mother', 'father', 'sister', 'brother', 'partner', 'child', 'grandparent', 'aunt', 'uncle', 'cousin', 'friend', 'other'];
const REL_KINDS = [
  { id: 'parent', label: 'Parent of', stroke: '#52525b' },
  { id: 'spouse', label: 'Married / Partner', stroke: '#16a34a' },
  { id: 'sibling', label: 'Sibling', stroke: '#2563eb' },
  { id: 'child', label: 'Child of', stroke: '#52525b' },
  { id: 'close', label: 'Close', stroke: '#16a34a', dash: '0' },
  { id: 'conflict', label: 'Conflict', stroke: '#dc2626', dash: '4 2' },
  { id: 'estranged', label: 'Estranged', stroke: '#a16207', dash: '6 4' },
  { id: 'cutoff', label: 'Cut-off', stroke: '#0f172a', dash: '2 6' },
];

function genId() { return Math.random().toString(36).slice(2, 9); }

export default function Genogram({ week = 6, testid = 'genogram' }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(null);   // { id, dx, dy }
  const [edgeDraft, setEdgeDraft] = useState({ from: '', to: '', kind: 'parent' });
  const [showLegend, setShowLegend] = useState(false);
  const dirtyRef = useRef(false);
  const svgRef = useRef(null);

  // Load
  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/self-work-genogram/${week}`);
        if (r.ok) {
          const d = await r.json();
          setNodes(d.nodes || []);
          setEdges(d.edges || []);
        }
      } catch {/* noop */}
      setLoading(false);
    })();
  }, [week]);

  // Autosave
  const save = useCallback(async () => {
    setSaving(true);
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/self-work-genogram/${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes, edges }),
      });
      dirtyRef.current = false;
    } catch {/* noop */}
    setSaving(false);
  }, [week, nodes, edges]);

  useEffect(() => {
    const id = setInterval(() => { if (dirtyRef.current) save(); }, 3000);
    return () => clearInterval(id);
  }, [save]);

  const addNode = (role) => {
    const newNode = {
      id: genId(),
      label: role.charAt(0).toUpperCase() + role.slice(1),
      role,
      gender: role === 'mother' || role === 'sister' ? 'female' : role === 'father' || role === 'brother' ? 'male' : 'unspec',
      addiction: false,
      deceased: false,
      x: 60 + ((nodes.length * 70) % 240),
      y: 60 + Math.floor(nodes.length / 4) * 90,
    };
    setNodes(n => [...n, newNode]);
    dirtyRef.current = true;
  };

  const updateNode = (id, patch) => {
    setNodes(n => n.map(node => node.id === id ? { ...node, ...patch } : node));
    dirtyRef.current = true;
  };

  const deleteNode = (id) => {
    setNodes(n => n.filter(node => node.id !== id));
    setEdges(e => e.filter(edge => edge.from !== id && edge.to !== id));
    dirtyRef.current = true;
  };

  const addEdge = () => {
    if (!edgeDraft.from || !edgeDraft.to || edgeDraft.from === edgeDraft.to) return;
    setEdges(e => [...e, { id: genId(), ...edgeDraft }]);
    setEdgeDraft({ from: '', to: '', kind: 'parent' });
    dirtyRef.current = true;
  };

  const deleteEdge = (id) => {
    setEdges(e => e.filter(edge => edge.id !== id));
    dirtyRef.current = true;
  };

  // Drag handling
  const onPointerDown = (e, node) => {
    const pt = svgRef.current?.getBoundingClientRect();
    if (!pt) return;
    setDrag({ id: node.id, dx: e.clientX - pt.left - node.x, dy: e.clientY - pt.top - node.y });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const pt = svgRef.current?.getBoundingClientRect();
    if (!pt) return;
    const x = Math.max(20, Math.min(pt.width - 20, e.clientX - pt.left - drag.dx));
    const y = Math.max(20, Math.min(pt.height - 20, e.clientY - pt.top - drag.dy));
    updateNode(drag.id, { x, y });
  };

  const onPointerUp = () => { if (drag) setDrag(null); };

  const symbolFor = (node) => {
    // Square = male, circle = female, diamond = unspec
    const stroke = node.addiction ? '#dc2626' : '#0f172a';
    const fill = node.deceased ? '#71717a' : '#fff';
    if (node.gender === 'male') return <rect x={node.x - 14} y={node.y - 14} width={28} height={28} fill={fill} stroke={stroke} strokeWidth={node.addiction ? 2.5 : 1.5} />;
    if (node.gender === 'female') return <circle cx={node.x} cy={node.y} r={15} fill={fill} stroke={stroke} strokeWidth={node.addiction ? 2.5 : 1.5} />;
    return <polygon points={`${node.x},${node.y - 16} ${node.x + 16},${node.y} ${node.x},${node.y + 16} ${node.x - 16},${node.y}`} fill={fill} stroke={stroke} strokeWidth={node.addiction ? 2.5 : 1.5} />;
  };

  if (loading) return <div className="text-center py-6 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-3" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Add</span>
        {ROLES.slice(0, 7).map(r => (
          <button
            key={r}
            onClick={() => addNode(r)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 hover:bg-zinc-100"
            data-testid={`genogram-add-${r}`}
          >
            <Plus className="w-3 h-3" /> {r}
          </button>
        ))}
        <button
          onClick={() => setShowLegend(v => !v)}
          className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 hover:bg-zinc-100"
          data-testid="genogram-legend-toggle"
        >
          <Info className="w-3 h-3" /> Legend
        </button>
        {saving && <span className="text-[10px] text-zinc-400 inline-flex items-center gap-1"><Save className="w-3 h-3" /> Saving</span>}
      </div>

      {showLegend && (
        <div className="bg-white border border-zinc-200 rounded-lg p-3 text-[11px] text-zinc-700 leading-relaxed space-y-1.5" data-testid="genogram-legend">
          <p><strong>Square</strong>  =  male  ·  <strong>Circle</strong>  =  female  ·  <strong>Diamond</strong>  =  unspecified</p>
          <p><strong>Filled grey</strong>  =  deceased  ·  <strong>Red outline</strong>  =  addiction history</p>
          <p>Relationship lines:  <span className="text-zinc-700">solid grey</span>  parent  ·  <span className="text-emerald-700">solid green</span>  married/partner/close  ·  <span className="text-blue-700">solid blue</span>  sibling  ·  <span className="text-rose-700">red dashed</span>  conflict  ·  <span className="text-amber-700">amber dashed</span>  estranged  ·  <span className="text-zinc-900">dotted</span>  cut-off</p>
        </div>
      )}

      {/* Canvas */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" style={{ touchAction: 'none' }}>
        <svg
          ref={svgRef}
          viewBox="0 0 360 320"
          className="w-full"
          style={{ height: 320 }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          data-testid="genogram-canvas"
        >
          {/* edges first so nodes render on top */}
          {edges.map(edge => {
            const a = nodes.find(n => n.id === edge.from);
            const b = nodes.find(n => n.id === edge.to);
            if (!a || !b) return null;
            const kind = REL_KINDS.find(k => k.id === edge.kind) || REL_KINDS[0];
            return (
              <g key={edge.id} onClick={() => deleteEdge(edge.id)} style={{ cursor: 'pointer' }}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={kind.stroke}
                  strokeWidth="1.8"
                  strokeDasharray={kind.dash || '0'}
                />
              </g>
            );
          })}
          {nodes.map(node => (
            <g key={node.id} onPointerDown={(e) => onPointerDown(e, node)} style={{ cursor: 'grab' }}>
              {symbolFor(node)}
              <text x={node.x} y={node.y + 30} textAnchor="middle" fontSize="10" fill="#27272a">{node.label}</text>
            </g>
          ))}
          {nodes.length === 0 && (
            <text x="180" y="160" textAnchor="middle" fontSize="11" fill="#a1a1aa">Add a person above to begin</text>
          )}
        </svg>
      </div>

      {/* People list (label edits + addiction/deceased flags) */}
      {nodes.length > 0 && (
        <div className="space-y-1.5">
          {nodes.map(node => (
            <div key={node.id} className="bg-white border border-zinc-200 rounded-lg p-2 flex items-center gap-2" data-testid={`genogram-node-${node.id}`}>
              <input
                value={node.label}
                onChange={(e) => updateNode(node.id, { label: e.target.value })}
                className="flex-1 text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-1"
                placeholder="Name or label"
              />
              <select
                value={node.gender}
                onChange={(e) => updateNode(node.id, { gender: e.target.value })}
                className="text-[10px] bg-white border border-zinc-200 rounded px-1.5 py-1"
              >
                <option value="male">male</option>
                <option value="female">female</option>
                <option value="unspec">unspec</option>
              </select>
              <label className="inline-flex items-center gap-1 text-[10px] text-zinc-700">
                <input type="checkbox" checked={!!node.addiction} onChange={(e) => updateNode(node.id, { addiction: e.target.checked })} /> addiction
              </label>
              <label className="inline-flex items-center gap-1 text-[10px] text-zinc-700">
                <input type="checkbox" checked={!!node.deceased} onChange={(e) => updateNode(node.id, { deceased: e.target.checked })} /> deceased
              </label>
              <button onClick={() => deleteNode(node.id)} className="text-zinc-400 hover:text-rose-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add edge */}
      {nodes.length >= 2 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-2 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Connect</span>
          <select value={edgeDraft.from} onChange={(e) => setEdgeDraft(d => ({ ...d, from: e.target.value }))} className="text-[11px] bg-white border border-zinc-200 rounded px-2 py-1">
            <option value="">From...</option>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
          <select value={edgeDraft.kind} onChange={(e) => setEdgeDraft(d => ({ ...d, kind: e.target.value }))} className="text-[11px] bg-white border border-zinc-200 rounded px-2 py-1">
            {REL_KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <select value={edgeDraft.to} onChange={(e) => setEdgeDraft(d => ({ ...d, to: e.target.value }))} className="text-[11px] bg-white border border-zinc-200 rounded px-2 py-1">
            <option value="">To...</option>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
          <button onClick={addEdge} className="text-[11px] px-2 py-1 rounded bg-zinc-950 text-white hover:bg-zinc-800" data-testid="genogram-add-edge">Add</button>
          <span className="text-[10px] text-zinc-400">tap a line on the canvas to remove it</span>
        </div>
      )}
    </div>
  );
}
